<template>
  <!-- MFC2-1626 — retours Diana des 26 et 27/08 : « la commande reste figée après fermeture de la
       fenêtre », « la réponse NO devrait avoir le même résultat que ton OK », « j'ai des pop up
       "Are you sure" dans tous les sens ».
       Trois tentatives précédentes ont échoué parce qu'elles traitaient le symptôme. Le défaut de
       fond était que la visibilité vivait dans `params.props.modelValue` et devait être resynchronisée
       à la main par deux watchers croisés. La visibilité est désormais une donnée partagée unique
       (`visible`), branchée en v-model : la modale et l'appelant ne peuvent plus diverger, quelle que
       soit l'origine de la fermeture (OK, NO, croix, Échap, clic sur le voile).
       Le vidage des paramètres est différé à @hidden, donc après la transition. -->
  <b-modal
    id="popConfirmModal"
    v-model="visible"
    v-bind="modalProps"
    @hidden="onHidden"
  >
    {{ paramsConfirm.content }}
    <app-form ref="formRef" tag="form">
      <div v-if="paramsConfirm.options.input">
        {{ $t('alert.delete_confirmation_permanently.validate_deletion') }}
        <app-input id="confirmDelete" v-model="confirDelete" class-group="mt-2" :rules="computedRules" hide-errors>
          <template #error="{ error }">
            <small v-if="error" class="text-danger">{{ $t('alert.delete_confirmation_permanently.validate_deletion') }}</small>
          </template>
        </app-input>
      </div>
    </app-form>
  </b-modal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import useBVModal from '@/composables/useBVModal'
import { useGlobalProperties } from '@/composables/useGlobalProperties'

defineOptions({
  name: 'MsgBoxConfirm',
})

const { t, locale } = useI18n()
const { paramsConfirm, visible, hide, reset } = useBVModal
const { _cloneDeep } = useGlobalProperties()

const defaultProps = {
  title: t('alert.delete_confirmation.title'),
  size: 'sm',
  okVariant: 'primary',
  okTitle: t('common.yes'),
  cancelTitle: t('common.no'),
  cancelVariant: 'outline-secondary',
  hideHeaderClose: false,
  centered: true,
  // MFC2-1626 — Diana : « il n'est pas nécessaire de figer l'écran quand on scroll la souris ».
  // Sans cela bootstrap-vue-next pose overflow:hidden sur le body pendant toute l'ouverture.
  bodyScrolling: true,
}

const formRef = ref(null)
const confirDelete = ref('')

// MFC2-1626 — la saisie de confirmation doit repartir vide a chaque ouverture. Elle etait conservee,
// si bien qu'une seconde suppression s'ouvrait avec SUPPRIMER deja saisi et pouvait etre validee
// sans que l'utilisateur ait rien tape.
const onHidden = () => {
  confirDelete.value = ''
  reset()
}

const modalProps = computed(() => {
  const props = { ...paramsConfirm.value.props }

  if (!paramsConfirm.value.options.input) {
    // NO, la croix et Échap ne sont volontairement pas interceptés : b-modal se ferme, v-model
    // repasse `visible` à faux et @hidden vide les paramètres. Aucune action API ni Front,
    // l'application continue sa vie — c'est exactement ce que demande Diana.
    return Object.assign(_cloneDeep(defaultProps), props)
  }

  const { onOk, ...restProps } = props

  return {
    ..._cloneDeep(defaultProps),
    ...restProps,
    onOk: evt => {
      // La saisie de confirmation doit être validée avant de laisser la modale se fermer.
      evt.preventDefault()
      formRef.value.validate().then(({ valid }) => {
        if (valid) {
          // hide() et non reset() : les paramètres restent en place le temps de la transition,
          // @hidden fera le ménage.
          hide()
          onOk()
        }
      })
    },
  }
})

// Génère la règle de suppression en fonction de l'anglais ou du français
// Anglais : DELETE
// Français : SUPPRIMER
const computedRules = computed(() => (locale.value === 'en' ? 'required|is:DELETE' : 'required|is:SUPPRIMER'))

</script>
